// ─────────────────────────────────────────────────────────────────────────────
// ExploradorModelosAdmin.tsx — tela de ADMIN dos "modelos de dados" (SQL) do
// Explorador (Fase 5). Dois modos no mesmo componente: LISTA (cards dos modelos
// cadastrados) e EDITOR (criar/editar um modelo — SQL + preview + mapeamento de
// colunas em dimensão/medida/ignorar). Consome estatisticasModelosApi; NÃO cria
// API. Embutido como aba na OrganizacaoPage pelo chamador (sem props/rotas).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  BeakerIcon,
  PencilSquareIcon,
  PlusIcon,
  TableCellsIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  estatisticasModelosApi,
  type ColunaMapeada,
  type ModeloPayload,
  type ModeloSalvoAdmin,
  type PapelColuna,
  type FmtMedida,
  type AggMedida,
  type PreviewResultado,
  type FiltroReutilizavel,
  type ParametroComValor,
  type TipoParametro,
  type OpcaoLista,
} from '../../../api/estatisticasModelos';
import { useToast } from '../../../components/ui/Toast';
import { PainelIris, ToggleIris } from '../../../components/ui/iris';
import { TIPOS_PARAMETRO, PADROES_POR_TIPO, padraoEhSelect } from './parametrosFiltro';

// btn* não vêm do index com o mesmo nome que o restante das telas usa; redeclaro
// localmente as 3 classes Íris (mesmas strings do ui/iris/index.ts) pra não
// depender de export que possa mudar.
const BTN_PRIMARIO =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-iris bg-accent text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SECUNDARIO =
  'inline-flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-iris bg-surface-2 border border-line text-tx hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const SELECT_IRIS =
  'h-8 rounded-iris-sm border border-line bg-surface px-2 text-sm text-tx focus:border-accent focus:outline-none disabled:opacity-50';
const INPUT_IRIS =
  'h-9 px-3 text-sm bg-surface-2 border border-line-strong rounded-iris-sm text-tx placeholder:text-tx-3 focus:outline-none focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-soft';

const FMT_OPCOES: { value: FmtMedida; label: string }[] = [
  { value: 'money', label: 'R$' },
  { value: 'int', label: 'Número' },
  { value: 'pct', label: '%' },
];
const AGG_OPCOES: { value: AggMedida; label: string }[] = [
  { value: 'sum', label: 'Soma' },
  { value: 'avg', label: 'Média' },
  { value: 'count', label: 'Contagem' },
  { value: 'min', label: 'Mín' },
  { value: 'max', label: 'Máx' },
];
/** type do <input> de valor de teste conforme o tipo do parâmetro. */
function inputTypeParam(tipo: TipoParametro): 'text' | 'number' | 'date' {
  if (tipo === 'numero') return 'number';
  if (tipo === 'data') return 'date';
  return 'text';
}

/**
 * Extrai nomes de parâmetros `@nome` do SQL e faz merge com a lista atual:
 * distinct case-insensitive (preserva a 1ª grafia encontrada). Parâmetros já
 * existentes (match por nome, case-insensitive) preservam label/tipo/valor; novos
 * entram com defaults; os que sumiram do SQL são descartados.
 */
/**
 * Nomes de parâmetro do SQL nas DUAS sintaxes — `@nome` e `:nome` (SQL bruto
 * colado do ERP usa dois-pontos). Literais '...' e comentários (linha e bloco)
 * saem antes do match: um '12:30' num literal não é parâmetro. `::` também não
 * (cast colado por engano). Espelha ConsultaSalvaService.NormalizarSintaxeParametros.
 */
function extrairNomesParametros(sql: string): string[] {
  const semRuido = sql
    .replace(/'(?:[^']|'')*'/g, "''")
    .replace(/--[^\n]*/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, ' ');
  const nomes: string[] = [];
  const re = /[@:](\w+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(semRuido)) !== null) {
    const anterior = m.index > 0 ? semRuido[m.index - 1] : ' ';
    if (semRuido[m.index] === ':' && (anterior === ':' || /\w/.test(anterior))) continue;
    nomes.push(m[1]);
  }
  return nomes;
}

function mergeParametros(sql: string, atuais: ParametroComValor[]): ParametroComValor[] {
  const porNome = new Map<string, ParametroComValor>();
  for (const p of atuais) porNome.set(p.nome.toLowerCase(), p);

  const vistos = new Set<string>();
  const resultado: ParametroComValor[] = [];
  for (const nome of extrairNomesParametros(sql)) {
    const chave = nome.toLowerCase();
    if (vistos.has(chave)) continue; // distinct case-insensitive, 1ª grafia vence
    vistos.add(chave);
    const anterior = porNome.get(chave);
    if (anterior) {
      // preserva label/tipo/valor/padrao + opcoesSql (SQL de opções do tipo 'lista')
      resultado.push({ ...anterior });
    } else {
      resultado.push({ nome, label: nome, tipo: 'texto', valor: '' });
    }
  }
  return resultado;
}

/** Extrai a mensagem de erro de validação (axios 400) do backend. */
function mensagemErro(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

/** Conta dim/measure de um mapeamento pra exibir no card. */
function contarPapeis(colunas: ColunaMapeada[]): { dims: number; measures: number } {
  let dims = 0;
  let measures = 0;
  for (const c of colunas) {
    if (c.role === 'dim') dims++;
    else if (c.role === 'measure') measures++;
  }
  return { dims, measures };
}

type EditandoState = ModeloSalvoAdmin | 'novo' | null;

export default function ExploradorModelosAdmin() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<EditandoState>(null);

  const { data: modelos, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['estatisticas', 'modelos', 'gerenciar'],
    queryFn: () => estatisticasModelosApi.gerenciar(),
    staleTime: 60 * 1000,
  });

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ['estatisticas', 'modelos'] });
  };

  const exportar = async (m: ModeloSalvoAdmin) => {
    try {
      const pacote = await estatisticasModelosApi.exportar(m.id);
      // Download client-side: o pacote é JSON puro; nome do arquivo carrega o
      // nome do modelo pra quem exporta vários não se perder.
      const blob = new Blob([JSON.stringify(pacote, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `modelo-${m.nome.replace(/[^\w\-]+/g, '_').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.erro(mensagemErro(e, 'Erro ao exportar o modelo.'));
    }
  };

  const importarArquivo = async (file: File | undefined) => {
    if (!file) return;
    try {
      const pacote = JSON.parse(await file.text());
      const r = await estatisticasModelosApi.importar(pacote);
      // Avisos do import (filtro homônimo mantido, modelo renomeado, inativo até
      // revisar) são informação de decisão — cada um vira um toast próprio.
      toast.sucesso(`Modelo "${r.nome}" importado.`);
      for (const aviso of r.avisos ?? []) toast.aviso ? toast.aviso(aviso) : toast.sucesso(aviso);
      recarregar();
    } catch (e) {
      toast.erro(mensagemErro(e, 'Arquivo inválido ou erro ao importar.'));
    }
  };

  const excluir = async (m: ModeloSalvoAdmin) => {
    if (!confirm(`Excluir o modelo "${m.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await estatisticasModelosApi.excluir(m.id);
      toast.sucesso('Modelo excluído.');
      recarregar();
    } catch (e) {
      toast.erro(mensagemErro(e, 'Erro ao excluir o modelo.'));
    }
  };

  // ── Modo EDITOR ───────────────────────────────────────────────────────────
  if (editando) {
    return (
      <EditorModelo
        modelo={editando === 'novo' ? null : editando}
        onCancelar={() => setEditando(null)}
        onSalvo={() => {
          setEditando(null);
          recarregar();
        }}
      />
    );
  }

  // ── Modo LISTA ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-tx tracking-[-0.01em]">Modelos de dados (SQL)</h2>
          <p className="text-[13px] text-tx-3 mt-0.5">
            Consultas SQL cadastradas que viram modelos do Explorador. Cada coluna do resultado é
            mapeada como dimensão, medida ou ignorada.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className={`${BTN_SECUNDARIO} h-9 px-3 text-xs cursor-pointer`}>
            <ArrowUpTrayIcon className="w-4 h-4" /> Importar
            <input
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => { importarArquivo(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          <button type="button" onClick={() => setEditando('novo')} className={BTN_PRIMARIO}>
            <PlusIcon className="w-4 h-4" /> Novo modelo
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-6 flex items-center justify-center text-tx-3">
          <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" /> Carregando…
        </div>
      ) : isError ? (
        <div className="rounded-iris border border-danger-border bg-danger-soft px-3 py-3 text-sm text-danger flex items-center justify-between gap-3">
          <span>Não foi possível carregar os modelos.</span>
          <button type="button" onClick={() => refetch()} className={`${BTN_SECUNDARIO} h-8 px-3 text-xs`}>
            <ArrowPathIcon className="w-3.5 h-3.5" /> Tentar de novo
          </button>
        </div>
      ) : !modelos || modelos.length === 0 ? (
        <PainelIris>
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <TableCellsIcon className="w-7 h-7 text-tx-3" />
            <p className="text-sm font-medium text-tx">Nenhum modelo cadastrado ainda</p>
            <p className="max-w-[420px] text-xs text-tx-3">
              Crie um modelo escrevendo um SELECT, testando o resultado e mapeando as colunas em
              dimensões e medidas.
            </p>
            <button type="button" onClick={() => setEditando('novo')} className={`${BTN_PRIMARIO} mt-1`}>
              <PlusIcon className="w-4 h-4" /> Novo modelo
            </button>
          </div>
        </PainelIris>
      ) : (
        <div className="space-y-3">
          {isFetching && (
            <div className="flex items-center gap-1.5 text-xs text-tx-3">
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> atualizando…
            </div>
          )}
          {modelos.map((m) => {
            const { dims, measures } = contarPapeis(m.mapeamento?.colunas ?? []);
            return (
              <div
                key={m.id}
                className="flex items-center gap-4 rounded-iris-lg border border-line bg-surface p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-tx truncate">{m.nome}</span>
                    {m.ativo ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold rounded-iris-sm bg-ok-soft text-ok">
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-semibold rounded-iris-sm bg-surface-2 text-tx-3">
                        Inativo
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[13px] text-tx-3">
                    {dims} {dims === 1 ? 'dimensão' : 'dimensões'} · {measures} {measures === 1 ? 'medida' : 'medidas'}
                    {m.criadoPor ? <> · {m.criadoPor}</> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => exportar(m)}
                    title="Baixa um .json pra importar em outro cliente"
                    className={`${BTN_SECUNDARIO} h-8 px-3 text-xs`}
                  >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Exportar
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditando(m)}
                    className={`${BTN_SECUNDARIO} h-8 px-3 text-xs`}
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => excluir(m)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-iris text-danger hover:bg-danger-soft transition-colors"
                  >
                    <TrashIcon className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FiltrosReutilizaveisSecao />
    </div>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────

interface EditorModeloProps {
  /** null = novo modelo; preenchido = edição. */
  modelo: ModeloSalvoAdmin | null;
  onCancelar: () => void;
  onSalvo: () => void;
}

/**
 * Detecta se a coluna é numérica olhando a amostra: todos os valores NÃO-nulos
 * precisam ser `number` (e tem que haver ao menos um não-nulo). Coluna toda nula
 * ou com texto cai como dimensão.
 */
/**
 * CRUD leve dos filtros reutilizáveis: o SQL de opções (ex.: tgerempresa) é
 * cadastrado UMA vez e referenciado por N modelos via select na linha do
 * parâmetro tipo 'lista' — antes era re-colado em cada modelo e divergia.
 */
function FiltrosReutilizaveisSecao() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: filtros = [], isLoading } = useQuery({
    queryKey: ['estatisticas', 'filtros', 'gerenciar'],
    queryFn: () => estatisticasModelosApi.filtrosGerenciar(),
    staleTime: 60 * 1000,
  });
  const [editando, setEditando] = useState<{ id: number | null; nome: string; sqlOpcoes: string } | null>(null);
  const [salvando, setSalvando] = useState(false);

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['estatisticas', 'filtros'] });

  const salvar = async () => {
    if (!editando) return;
    setSalvando(true);
    try {
      if (editando.id != null) await estatisticasModelosApi.filtroEditar(editando.id, editando.nome, editando.sqlOpcoes);
      else await estatisticasModelosApi.filtroCriar(editando.nome, editando.sqlOpcoes);
      toast.sucesso('Filtro salvo.');
      setEditando(null);
      recarregar();
    } catch (e) {
      toast.erro(mensagemErro(e, 'Erro ao salvar o filtro.'));
    } finally {
      setSalvando(false);
    }
  };

  const excluirFiltro = async (f: FiltroReutilizavel) => {
    if (!confirm(`Excluir o filtro "${f.nome}"? Modelos que o referenciam ficarão com as opções vazias.`)) return;
    try {
      await estatisticasModelosApi.filtroExcluir(f.id);
      toast.sucesso('Filtro excluído.');
      recarregar();
    } catch (e) {
      toast.erro(mensagemErro(e, 'Erro ao excluir o filtro.'));
    }
  };

  return (
    <div className="mt-6 rounded-iris-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-tx">Filtros reutilizáveis</h3>
          <p className="text-[12px] text-tx-3 mt-0.5">
            SQL de opções (ex.: empresas, marcas) cadastrado uma vez e usado por vários modelos —
            no parâmetro tipo Lista, escolha o filtro em vez de colar o SQL de novo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditando({ id: null, nome: '', sqlOpcoes: '' })}
          className={`${BTN_SECUNDARIO} h-8 px-3 text-xs shrink-0`}
        >
          <PlusIcon className="w-3.5 h-3.5" /> Novo filtro
        </button>
      </div>

      {isLoading ? (
        <div className="mt-3 text-[13px] text-tx-3">Carregando…</div>
      ) : filtros.length === 0 && !editando ? (
        <div className="mt-3 text-[13px] text-tx-3 italic">Nenhum filtro cadastrado ainda.</div>
      ) : (
        <div className="mt-3 space-y-1.5">
          {filtros.map((f) => (
            <div key={f.id} className="flex items-center gap-3 rounded-iris border border-line px-3 py-2">
              <span className="text-[13px] font-medium text-tx">{f.nome}</span>
              <code className="flex-1 truncate text-[11px] text-tx-3">{f.sqlOpcoes}</code>
              <button type="button" onClick={() => setEditando({ id: f.id, nome: f.nome, sqlOpcoes: f.sqlOpcoes })}
                className="text-[12px] font-semibold text-tx-2 hover:text-tx">Editar</button>
              <button type="button" onClick={() => excluirFiltro(f)}
                className="text-[12px] font-semibold text-danger hover:opacity-80">Excluir</button>
            </div>
          ))}
        </div>
      )}

      {editando && (
        <div className="mt-3 rounded-iris border border-accent-border bg-surface-2 p-3 space-y-2">
          <input
            type="text"
            value={editando.nome}
            onChange={(e) => setEditando((prev) => prev && { ...prev, nome: e.target.value })}
            placeholder="Nome do filtro (ex.: Empresas)"
            className={`w-full ${INPUT_IRIS} h-8`}
          />
          <textarea
            value={editando.sqlOpcoes}
            onChange={(e) => setEditando((prev) => prev && { ...prev, sqlOpcoes: e.target.value })}
            rows={3}
            spellCheck={false}
            placeholder="SELECT EMPRESA, NOME FROM TGEREMPRESA"
            className="w-full resize-y rounded-iris-sm border border-line-strong bg-surface px-2 py-1.5 font-mono text-[12px] text-tx placeholder:text-tx-3 focus:border-accent focus:outline-none"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={salvar} disabled={salvando} className={`${BTN_PRIMARIO} h-8 px-3 text-xs`}>
              {salvando ? 'Salvando…' : 'Salvar filtro'}
            </button>
            <button type="button" onClick={() => setEditando(null)} className={`${BTN_SECUNDARIO} h-8 px-3 text-xs`}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function colunaEhNumerica(field: string, linhas: Array<Record<string, unknown>>): boolean {

  let viuValor = false;
  for (const linha of linhas) {
    const v = linha[field];
    if (v === null || v === undefined) continue;
    viuValor = true;
    if (typeof v !== 'number') return false;
  }
  return viuValor;
}

/**
 * Monta as linhas de mapeamento a partir do preview. Numérica → medida (int/soma);
 * senão dimensão. Em edição, faz merge com o mapeamento salvo (preserva
 * role/label/fmt/agg por field) pra não perder a configuração anterior.
 */
function montarMapeamento(
  preview: PreviewResultado,
  salvo: ColunaMapeada[] | undefined,
): ColunaMapeada[] {
  const porField = new Map<string, ColunaMapeada>();
  for (const c of salvo ?? []) porField.set(c.field, c);

  return preview.colunas.map((field) => {
    const anterior = porField.get(field);
    if (anterior) {
      return {
        field,
        role: anterior.role,
        label: anterior.label || field,
        fmt: anterior.fmt,
        agg: anterior.agg,
      };
    }
    if (colunaEhNumerica(field, preview.linhas)) {
      return { field, role: 'measure', label: field, fmt: 'int', agg: 'sum' };
    }
    return { field, role: 'dim', label: field };
  });
}

function EditorModelo({ modelo, onCancelar, onSalvo }: EditorModeloProps) {
  const toast = useToast();
  const ehNovo = modelo === null;

  const [nome, setNome] = useState(modelo?.nome ?? '');
  const [sql, setSql] = useState(modelo?.sql ?? '');
  const [ativo, setAtivo] = useState(modelo?.ativo ?? true);

  const [colunas, setColunas] = useState<ColunaMapeada[]>(modelo?.mapeamento?.colunas ?? []);
  const [parametros, setParametros] = useState<ParametroComValor[]>(modelo?.parametros ?? []);
  const [amostra, setAmostra] = useState<PreviewResultado | null>(null);

  const [testando, setTestando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroSql, setErroSql] = useState<string | null>(null);

  // Filtros reutilizáveis cadastrados — alimentam o select de fonte do tipo 'lista'.
  const { data: filtrosReutilizaveis = [] } = useQuery({
    queryKey: ['estatisticas', 'filtros', 'gerenciar'],
    queryFn: () => estatisticasModelosApi.filtrosGerenciar(),
    staleTime: 60 * 1000,
  });

  const podeMapear = colunas.length > 0;

  // Detecção de @parâmetros conforme o SQL muda (debounce simples). O merge
  // preserva label/tipo/valor dos já existentes e remove os que sumiram do SQL.
  useEffect(() => {
    const t = setTimeout(() => {
      setParametros((prev) => mergeParametros(sql, prev));
    }, 400);
    return () => clearTimeout(t);
  }, [sql]);

  const testarSql = async () => {
    if (!sql.trim()) {
      toast.aviso('Escreva um SQL antes de testar.');
      return;
    }
    setTestando(true);
    setErroSql(null);
    // Detecta/atualiza os parâmetros agora (não espera o debounce) e usa a mesma
    // lista no preview — passando os valores de teste; @param sem valor binda NULL.
    const paramsAtuais = mergeParametros(sql, parametros);
    setParametros(paramsAtuais);
    try {
      const preview = await estatisticasModelosApi.preview(sql, paramsAtuais);
      setAmostra(preview);
      // Merge com o mapeamento já existente (edição) ou com o que estiver em tela.
      const base = colunas.length > 0 ? colunas : modelo?.mapeamento?.colunas;
      setColunas(montarMapeamento(preview, base));
      if (preview.colunas.length === 0) {
        toast.aviso('A consulta não retornou colunas.');
      }
    } catch (e) {
      setAmostra(null);
      setErroSql(mensagemErro(e, 'Erro ao executar o SQL.'));
    } finally {
      setTestando(false);
    }
  };

  const atualizarColuna = (field: string, patch: Partial<ColunaMapeada>) => {
    setColunas((prev) =>
      prev.map((c) => {
        if (c.field !== field) return c;
        const next: ColunaMapeada = { ...c, ...patch };
        // Ao virar medida sem fmt/agg definidos, aplica defaults sensatos.
        if (next.role === 'measure') {
          if (!next.fmt) next.fmt = 'int';
          if (!next.agg) next.agg = 'sum';
        } else {
          // Dim/ignore não carregam fmt/agg (mantém o payload limpo).
          delete next.fmt;
          delete next.agg;
        }
        return next;
      }),
    );
  };

  const atualizarParametro = (nome: string, patch: Partial<ParametroComValor>) => {
    setParametros((prev) => prev.map((p) => (p.nome === nome ? { ...p, ...patch } : p)));
  };

  const salvar = async () => {
    if (!nome.trim()) {
      toast.aviso('Informe um nome para o modelo.');
      return;
    }
    const mapeadas = colunas.filter((c) => c.role === 'dim' || c.role === 'measure');
    if (mapeadas.length === 0) {
      toast.aviso('Mapeie ao menos uma coluna como dimensão ou medida.');
      return;
    }
    const payload: ModeloPayload = {
      nome: nome.trim(),
      sql,
      mapeamento: { colunas },
      // Persiste só a definição (sem o valor de teste, que é local do preview).
      // Listas levam também o SQL de opções que popula o multiselect.
      // ⚠ Mapa EXPLÍCITO: campo novo em ParametroModelo precisa entrar AQUI, senão
      // some no salvar — foi o bug do filtroId/obrigatório (2026-08-30): a UI
      // setava, o payload descartava, e o reload voltava como se nada tivesse sido
      // escolhido.
      parametros: parametros.map((p) => ({
        nome: p.nome,
        label: p.label,
        tipo: p.tipo,
        padrao: p.padrao ?? '',
        obrigatorio: p.obrigatorio ?? false,
        ...(p.tipo === 'lista' ? { opcoesSql: p.opcoesSql ?? '' } : {}),
        ...(p.tipo === 'lista' && p.filtroId ? { filtroId: p.filtroId } : {}),
      })),
      ativo,
    };
    setSalvando(true);
    setErroSql(null);
    try {
      if (ehNovo) await estatisticasModelosApi.criar(payload);
      else await estatisticasModelosApi.atualizar(modelo.id, payload);
      toast.sucesso(ehNovo ? 'Modelo criado.' : 'Modelo atualizado.');
      onSalvo();
    } catch (e) {
      // Erro 400 normalmente é validação do SQL — destaca no aviso vermelho.
      const msg = mensagemErro(e, 'Erro ao salvar o modelo.');
      setErroSql(msg);
      toast.erro(msg);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-tx tracking-[-0.01em]">
            {ehNovo ? 'Novo modelo' : `Editar — ${modelo.nome}`}
          </h2>
          <p className="text-[13px] text-tx-3 mt-0.5">
            Escreva o SELECT, teste o resultado e mapeie as colunas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancelar} disabled={salvando} className={BTN_SECUNDARIO}>
            Cancelar
          </button>
          <button type="button" onClick={salvar} disabled={salvando} className={BTN_PRIMARIO}>
            {salvando && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Identificação + estado */}
      <PainelIris>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-tx-2 mb-1">Nome</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Vendas por marca"
              className={`w-full ${INPUT_IRIS}`}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium text-tx">Ativo</div>
              <div className="text-[13px] text-tx-3 mt-0.5">
                Modelos inativos não aparecem no seletor do Explorador.
              </div>
            </div>
            <ToggleIris ativo={ativo} onChange={setAtivo} aria-label="Modelo ativo" />
          </div>
        </div>
      </PainelIris>

      {/* SQL + teste */}
      <PainelIris titulo="Consulta SQL" descricao="A consulta passa por validação no servidor ao testar/salvar.">
        <textarea
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          rows={8}
          spellCheck={false}
          placeholder="SELECT ... FROM ..."
          className="w-full resize-y rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-2 font-mono text-[13px] leading-relaxed text-tx placeholder:text-tx-3 focus:bg-surface focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
        />
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={testarSql} disabled={testando} className={BTN_SECUNDARIO}>
            {testando ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <BeakerIcon className="w-4 h-4" />}
            {testando ? 'Testando…' : 'Testar SQL'}
          </button>
          {amostra && !erroSql && (
            <span className="text-xs text-tx-3">
              {amostra.colunas.length} coluna(s) · {amostra.linhas.length} linha(s) na amostra
              {amostra.truncado ? ' (truncado)' : ''}
            </span>
          )}
        </div>

        {erroSql && (
          <div className="mt-3 rounded-iris border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
            {erroSql}
          </div>
        )}
      </PainelIris>

      {/* Parâmetros (filtros) — detectados a partir de @nome no SQL */}
      {parametros.length > 0 && (
        <PainelIris
          titulo="Parâmetros (filtros)"
          descricao="Detectados dos @nome ou :nome no SQL. Viram filtros no Explorador. O valor de teste é usado só no preview."
        >
          <div className="overflow-x-auto rounded-iris-sm border border-line">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-tx-2">
                  <th className="px-3 py-2 font-semibold border-b border-line">Parâmetro</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Rótulo</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Tipo</th>
                  <th className="px-3 py-2 font-semibold border-b border-line" title="Marcado: o Explorador exige preencher antes de carregar">Obrig.</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Padrão</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Valor de teste / Opções</th>
                </tr>
              </thead>
              <tbody>
                {parametros.map((p) => (
                  <LinhaParametro key={p.nome} p={p} onAtualizar={atualizarParametro} filtros={filtrosReutilizaveis} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[12px] text-tx-3">
            Parâmetros sem valor de teste são enviados como NULL no preview (pode retornar 0 linhas —
            as colunas ainda são detectadas).
          </p>
          <p className="mt-1 text-[12px] text-tx-3">
            <span className="font-semibold text-tx-2">Filtro opcional:</span> embrulhe o predicado em
            {' '}<code className="rounded bg-surface-2 px-1">{'/*[marca]*/ and f.marca in (@marca) /*[/marca]*/'}</code> —
            com o filtro vazio o bloco inteiro sai do SQL (não restringe nada); preenchido, só os marcadores caem.
          </p>
        </PainelIris>
      )}

      {/* Amostra (somente leitura) */}
      {amostra && amostra.colunas.length > 0 && (
        <PainelIris titulo="Amostra do resultado" descricao="Conferência das primeiras linhas — não editável.">
          <div className="overflow-x-auto rounded-iris-sm border border-line">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="bg-surface-2">
                  {amostra.colunas.map((c) => (
                    <th
                      key={c}
                      className="whitespace-nowrap px-3 py-2 text-left font-semibold text-tx-2 border-b border-line"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {amostra.linhas.slice(0, 8).map((linha, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    {amostra.colunas.map((c) => (
                      <td key={c} className="whitespace-nowrap px-3 py-1.5 text-tx">
                        {formatarCelula(linha[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PainelIris>
      )}

      {/* Mapeamento das colunas */}
      {podeMapear && (
        <PainelIris
          titulo="Mapeamento das colunas"
          descricao="Defina o papel de cada coluna. Medidas precisam de formato e agregação."
        >
          <div className="overflow-x-auto rounded-iris-sm border border-line">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="bg-surface-2 text-left text-tx-2">
                  <th className="px-3 py-2 font-semibold border-b border-line">Coluna</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Papel</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Rótulo</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Formato</th>
                  <th className="px-3 py-2 font-semibold border-b border-line">Agregação</th>
                </tr>
              </thead>
              <tbody>
                {colunas.map((c) => (
                  <tr key={c.field} className="border-b border-line last:border-0 align-middle">
                    <td className="px-3 py-2 font-mono text-tx whitespace-nowrap">{c.field}</td>
                    <td className="px-3 py-2">
                      <select
                        value={c.role}
                        onChange={(e) =>
                          atualizarColuna(c.field, { role: e.target.value as PapelColuna })
                        }
                        className={SELECT_IRIS}
                      >
                        <option value="dim">Dimensão</option>
                        <option value="measure">Medida</option>
                        <option value="ignore">Ignorar</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={c.label}
                        onChange={(e) => atualizarColuna(c.field, { label: e.target.value })}
                        placeholder={c.field}
                        className={`w-full min-w-[140px] ${INPUT_IRIS} h-8`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {c.role === 'measure' ? (
                        <select
                          value={c.fmt ?? 'int'}
                          onChange={(e) =>
                            atualizarColuna(c.field, { fmt: e.target.value as FmtMedida })
                          }
                          className={SELECT_IRIS}
                        >
                          {FMT_OPCOES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-tx-3">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {c.role === 'measure' ? (
                        <select
                          value={c.agg ?? 'sum'}
                          onChange={(e) =>
                            atualizarColuna(c.field, { agg: e.target.value as AggMedida })
                          }
                          className={SELECT_IRIS}
                        >
                          {AGG_OPCOES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-tx-3">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[12px] text-tx-3">
            Pelo menos uma coluna precisa ser dimensão ou medida pra salvar.
          </p>
        </PainelIris>
      )}
    </div>
  );
}

// ── Linha de parâmetro ─────────────────────────────────────────────────────────

/** Resultado local do "Testar opções" de um parâmetro lista. */
interface TesteOpcoes {
  n: number;
  amostra: string; // até 3 primeiras "valor — label"
  erro?: string;
}

interface LinhaParametroProps {
  filtros: FiltroReutilizavel[];
  p: ParametroComValor;
  onAtualizar: (nome: string, patch: Partial<ParametroComValor>) => void;
}

/**
 * Uma linha da tabela de parâmetros. Mantém estado LOCAL do teste de opções
 * (tipo 'lista') — número de opções, amostra e erro — pra não poluir o state do
 * editor. Tipos texto/número/data exibem o input simples de "Valor de teste";
 * 'lista' troca essa célula pelo editor do SQL de opções + botão de teste.
 */
function LinhaParametro({ p, onAtualizar, filtros }: LinhaParametroProps) {
  const ehLista = p.tipo === 'lista';
  const [testeOpcoes, setTesteOpcoes] = useState<TesteOpcoes | null>(null);
  const [testandoOpcoes, setTestandoOpcoes] = useState(false);

  const testarOpcoes = async () => {
    // Com filtro cadastrado selecionado, o teste roda o SQL DO FILTRO — é ele
    // que o Explorador vai executar.
    const sqlOpcoes = (p.filtroId
      ? filtros.find((f) => f.id === p.filtroId)?.sqlOpcoes
      : p.opcoesSql) ?? '';
    if (!sqlOpcoes.trim()) {
      setTesteOpcoes({ n: 0, amostra: '', erro: 'Escreva o SQL de opções antes de testar.' });
      return;
    }
    setTestandoOpcoes(true);
    try {
      const opcoes: OpcaoLista[] = await estatisticasModelosApi.opcoesPreview(sqlOpcoes);
      const amostra = opcoes
        .slice(0, 3)
        .map((o) => `${o.valor} — ${o.label}`)
        .join(', ');
      setTesteOpcoes({ n: opcoes.length, amostra });
    } catch (e) {
      setTesteOpcoes({ n: 0, amostra: '', erro: mensagemErro(e, 'Erro ao executar o SQL de opções.') });
    } finally {
      setTestandoOpcoes(false);
    }
  };

  return (
    <tr className="border-b border-line last:border-0 align-top">
      <td className="px-3 py-2 font-mono text-tx whitespace-nowrap">@{p.nome}</td>
      <td className="px-3 py-2">
        <input
          type="text"
          value={p.label}
          onChange={(e) => onAtualizar(p.nome, { label: e.target.value })}
          placeholder={p.nome}
          className={`w-full min-w-[140px] ${INPUT_IRIS} h-8`}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={p.tipo}
          onChange={(e) =>
            // reseta o padrão ao trocar o tipo: um token válido pro tipo antigo
            // (ex.: 'inicioMes' de data) não vale pro novo.
            onAtualizar(p.nome, { tipo: e.target.value as TipoParametro, padrao: '' })
          }
          className={SELECT_IRIS}
        >
          {TIPOS_PARAMETRO.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={p.obrigatorio ?? false}
          onChange={(e) => onAtualizar(p.nome, { obrigatorio: e.target.checked })}
          className="h-4 w-4 accent-[var(--accent)]"
          aria-label={`Obrigatório: ${p.nome}`}
        />
      </td>
      <td className="px-3 py-2">
        {padraoEhSelect(p.tipo) ? (
          <select
            value={p.padrao ?? ''}
            onChange={(e) => onAtualizar(p.nome, { padrao: e.target.value })}
            className={SELECT_IRIS}
          >
            {(PADROES_POR_TIPO[p.tipo] ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={p.padrao ?? ''}
            onChange={(e) => onAtualizar(p.nome, { padrao: e.target.value })}
            placeholder="valor padrão (opcional)"
            className={`w-full min-w-[140px] ${INPUT_IRIS} h-8`}
          />
        )}
      </td>
      <td className="px-3 py-2">
        {ehLista ? (
          <div className="flex flex-col gap-2 min-w-[280px]">
            {/* Fonte das opções: filtro CADASTRADO (reutilizável — o mesmo
               tgerempresa não precisa ser re-colado em cada modelo) ou SQL
               próprio deste parâmetro. filtroId vence no backend. */}
            <select
              value={p.filtroId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onAtualizar(p.nome, v ? { filtroId: Number(v) } : { filtroId: undefined });
              }}
              className={SELECT_IRIS}
            >
              <option value="">SQL próprio deste parâmetro…</option>
              {filtros.map((f) => (
                <option key={f.id} value={f.id}>Filtro cadastrado: {f.nome}</option>
              ))}
            </select>
            {!p.filtroId && (
            <textarea
              value={p.opcoesSql ?? ''}
              onChange={(e) => onAtualizar(p.nome, { opcoesSql: e.target.value })}
              rows={3}
              spellCheck={false}
              placeholder="SELECT CODIGO, FANTASIA FROM TGEREMPRESA"
              className="w-full resize-y rounded-iris-sm border border-line-strong bg-surface-2 px-2 py-1.5 font-mono text-[12px] leading-relaxed text-tx placeholder:text-tx-3 focus:bg-surface focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
            />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={testarOpcoes}
                disabled={testandoOpcoes}
                className={`${BTN_SECUNDARIO} h-8 px-3 text-xs`}
              >
                {testandoOpcoes ? (
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <BeakerIcon className="w-3.5 h-3.5" />
                )}
                {testandoOpcoes ? 'Testando…' : 'Testar opções'}
              </button>
              {testeOpcoes && !testeOpcoes.erro && (
                <span className="text-[12px] text-tx-3">
                  {testeOpcoes.n} opção(ões)
                  {testeOpcoes.amostra ? <span className="text-tx-2"> · {testeOpcoes.amostra}</span> : null}
                </span>
              )}
            </div>
            {testeOpcoes?.erro && (
              <span className="text-[12px] text-danger">{testeOpcoes.erro}</span>
            )}
            <span className="text-[12px] text-tx-3">
              No SQL principal use <code className="font-mono text-tx-2">IN (@{p.nome})</code> para
              listas.
            </span>
            <input
              type="text"
              value={p.valor ?? ''}
              onChange={(e) => onAtualizar(p.nome, { valor: e.target.value })}
              placeholder="valor de teste p/ o preview (ex.: 01 ou 01,02)"
              className={`w-full ${INPUT_IRIS} h-8`}
            />
          </div>
        ) : (
          <input
            type={inputTypeParam(p.tipo)}
            value={p.valor ?? ''}
            onChange={(e) => onAtualizar(p.nome, { valor: e.target.value })}
            className={`w-full min-w-[140px] ${INPUT_IRIS} h-8`}
          />
        )}
      </td>
    </tr>
  );
}

/** Renderiza um valor da amostra de forma legível (sem quebrar com objetos). */
function formatarCelula(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString('pt-BR');
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
